import { Box, Card, IconButton, Tooltip, Typography, Grid, CardMedia, CardContent, CardActions, Chip } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { ContentCopy, Delete, OpenInNew } from '@mui/icons-material';
import Swal from 'sweetalert2';
import Loading from '../../../../components/Loader/Loading';
import NoRowsLayout from '../../../../components/Layout/NoRowsLayout';
import { MediaServices } from '../../MediaServices';

const VideoList = ({ refreshTrigger, currentTab }) => {
    const [videoList, setVideoList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRowCount, setTotalRowCount] = useState(0);

    const fetchVideoList = useCallback(async () => {
        setLoading(true);
        try {
            const path = currentTab === 'whatsapp' ? 'whatsapp/video' : 'video';
            const response = await MediaServices.fetchVideoList(path);
            if (response.success === '1' || response.success === 1) {
                // Map the response to include id for each item
                const videosWithId = (response.data || []).map((video, index) => ({
                    ...video,
                    id: video.id || index + 1,
                    createdAt: video.lastModified
                }));
                setVideoList(videosWithId);
                setTotalRowCount(response.total || response.data?.length || 0);
            } else {
                console.error('Failed to fetch videos:', response.message);
            }
        } catch (error) {
            console.error('Error fetching videos:', error);
        } finally {
            setLoading(false);
        }
    }, [currentTab]);

    useEffect(() => {
        fetchVideoList();
    }, [refreshTrigger, fetchVideoList]);

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            Swal.fire({
                icon: 'success',
                title: 'Copied!',
                text: 'Video URL copied to clipboard',
                timer: 1500,
                showConfirmButton: false,
            });
        }).catch(() => {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: 'Could not copy URL to clipboard',
            });
        });
    };

    const handleOpenLink = (url) => {
        window.open(url, '_blank');
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This video will be permanently deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                const response = await MediaServices.deleteVideo(id);
                if (response.success === '1' || response.success === 1) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Video has been deleted',
                        timer: 1500,
                    });
                    fetchVideoList();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: response.message || 'Failed to delete video',
                    });
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Something went wrong while deleting',
                });
            }
        }
    };

    return (
        <Card sx={{ height: '100%' }}>
            <Box sx={{ p: '.5% 1%', border: '2px solid lightGray', bgcolor: 'lightGray',display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Video Gallery</Typography>
                <Chip 
                    label={`${totalRowCount} videos`} 
                    size="small" 
                    sx={{ fontSize: '0.75rem' }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <Loading />
                </Box>
            ) : videoList.length === 0 ? (
                <Box sx={{ minHeight: 400 }}>
                    <NoRowsLayout />
                </Box>
            ) : (
                <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                        {videoList.map((video) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <CardMedia
                                        component="video"
                                        src={video.url}
                                        sx={{ 
                                            height: { xs: 180, sm: 200, md: 220 },
                                            bgcolor: '#000',
                                            cursor: 'pointer'
                                        }}
                                        controls
                                        preload="metadata"
                                    />
                                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                        <Tooltip title={video.fileName}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 500,
                                                    mb: 1.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {video.fileName}
                                            </Typography>
                                        </Tooltip>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Size:
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                    {video.size ? (video.size / (1024 * 1024)).toFixed(2) + ' MB' : '-'}
                                                </Typography>
                                            </Box>
                                            {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Uploaded:
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                    {video.createdAt ? new Date(video.createdAt).toLocaleDateString() : '-'}
                                                </Typography>
                                            </Box> */}
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Copy URL">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleCopyLink(video.url)}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Open in New Tab">
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleOpenLink(video.url)}
                                                >
                                                    <OpenInNew fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {/* <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(video.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip> */}
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Card>
    );
};

export default VideoList;
